import { memo, useId } from "react";
import { useReducedMotion } from "motion/react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ANIM_MS,
  AXIS_TICK,
  CURSOR_FILL,
  CURSOR_LINE,
  formatCompact,
  truncateLabel,
} from "./chartTheme";
import { CartesianLegend, CartesianTooltip } from "./ChartChrome";
import {
  categoryLabelLayout,
  isHorizontal,
  plotHeight,
  xAxisBand,
  type CategoryLabelLayout,
  type NormalizedChart,
} from "./resolveChart";

const cartesianMargin = { top: 4, right: 10, left: 0, bottom: 0 } as const;

function axisLabel(value: string | undefined, axis: "x" | "y") {
  if (!value) return undefined;
  return axis === "x"
    ? { value, position: "insideBottom" as const, offset: -2, fontSize: 11, fill: "#94a3b8" }
    : { value, angle: -90, position: "insideLeft" as const, fontSize: 11, fill: "#94a3b8" };
}

function wrapLabel(text: string, charsPerLine: number, maxLines: number): string[] {
  const clean = text.trim();
  if (!clean) return [""];
  const words = clean.split(/\s+/);
  const lines: string[] = [];
  let cur = "";
  let i = 0;
  for (; i < words.length; i++) {
    const next = cur ? `${cur} ${words[i]}` : words[i];
    if (next.length <= charsPerLine || !cur) {
      cur = next;
    } else {
      lines.push(cur);
      cur = words[i];
      if (lines.length >= maxLines) {
        cur = "";
        break;
      }
    }
  }
  if (cur && lines.length < maxLines) {
    lines.push(cur);
    i = words.length;
  }
  if (i < words.length && lines.length) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = `${last.slice(0, Math.max(1, charsPerLine - 1))}…`;
  }
  return lines.map((l) => (l.length > charsPerLine ? `${l.slice(0, charsPerLine - 1)}…` : l));
}

function CategoryTick({
  x = 0,
  y = 0,
  payload,
  layout,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
  layout: CategoryLabelLayout;
}) {
  const raw = String(payload?.value ?? "");
  if (layout.angled) {
    const text =
      raw.length > layout.charsPerLine ? `${raw.slice(0, layout.charsPerLine - 1)}…` : raw;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          className="fill-muted-foreground"
          transform="rotate(-35)"
          textAnchor="end"
          dy="0.71em"
          fontSize={11}
          fontWeight={500}
        >
          <title>{raw}</title>
          {text}
        </text>
      </g>
    );
  }
  const lines = wrapLabel(raw, layout.charsPerLine, layout.lines);
  return (
    <g transform={`translate(${x},${y})`}>
      <text className="fill-muted-foreground" textAnchor="middle" fontSize={11} fontWeight={500}>
        <title>{raw}</title>
        {lines.map((ln, idx) => (
          <tspan key={idx} x={0} dy={idx === 0 ? "0.8em" : "1.05em"}>
            {ln}
          </tspan>
        ))}
      </text>
    </g>
  );
}

export const CartesianChart = memo(function CartesianChart({ chart }: { chart: NormalizedChart }) {
  const reduce = useReducedMotion();
  const uid = useId().replace(/:/g, "");
  const horizontal = isHorizontal(chart.kind);

  const anim = {
    isAnimationActive: !reduce,
    animationDuration: ANIM_MS,
    animationEasing: "ease-out" as const,
  };

  // resolveChart reserves this height and picks rotate-vs-wrap (wrapping collides when many) - no shift.
  const height = plotHeight(chart);

  const longestCat = chart.rows.reduce(
    (m, r) => Math.max(m, String(r[chart.categoryKey] ?? "").length),
    0,
  );
  const yAxisWidth = Math.min(172, Math.max(56, Math.round(longestCat * 6.4 + 16)));

  const isBars = chart.kind === "bar";
  // Px reserved for the category axis. catAxisProps is only ever spread onto <XAxis>,
  // so this is an X-axis height, never a Y-axis width.
  const xAxisHeight = xAxisBand(chart);
  const labelLayout = categoryLabelLayout(chart);
  const categoryTick = <CategoryTick layout={labelLayout} />;
  const catAxisProps = {
    dataKey: chart.categoryKey,
    tick: categoryTick,
    tickLine: false,
    axisLine: false,
    height: xAxisHeight,
    tickMargin: 8,
    interval: (labelLayout.angled || isBars ? 0 : "preserveStartEnd") as 0 | "preserveStartEnd",
    label: axisLabel(chart.xLabel, "x"),
  };

  const numericTick = { tick: AXIS_TICK, tickLine: false, axisLine: false } as const;
  const catTick = (v: string | number) => truncateLabel(v, 22);
  const cartesianTooltip = (
    <CartesianTooltip unit={chart.unit} singleSeries={chart.singleSeries} total={chart.total} />
  );

  let body: React.ReactElement;

  if (chart.kind === "bar" || horizontal) {
    body = (
      <BarChart data={chart.rows} layout={horizontal ? "vertical" : "horizontal"} margin={cartesianMargin}>
        <CartesianGrid strokeDasharray="3 3" horizontal={!horizontal} vertical={horizontal} />
        {horizontal ? (
          <>
            <XAxis type="number" {...numericTick} tickFormatter={formatCompact} label={axisLabel(chart.xLabel, "x")} />
            <YAxis type="category" dataKey={chart.categoryKey} {...numericTick} width={yAxisWidth} tickFormatter={catTick} />
          </>
        ) : (
          <>
            <XAxis {...catAxisProps} />
            <YAxis type="number" {...numericTick} width={40} tickFormatter={formatCompact} label={axisLabel(chart.yLabel, "y")} />
          </>
        )}
        <Tooltip cursor={{ fill: CURSOR_FILL }} content={cartesianTooltip} />
        {chart.singleSeries ? (
          <Bar
            dataKey={chart.series[0].key}
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            maxBarSize={horizontal ? 26 : 40}
            {...anim}
          >
            {chart.rows.map((r, i) => (
              <Cell key={i} fill={r.fill} />
            ))}
          </Bar>
        ) : (
          chart.series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              stackId={chart.stacked ? "stack" : undefined}
              radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
              maxBarSize={horizontal ? 22 : 32}
              {...anim}
            />
          ))
        )}
      </BarChart>
    );
  } else if (chart.kind === "area") {
    body = (
      <AreaChart data={chart.rows} margin={cartesianMargin}>
        <defs>
          {chart.series.map((s) => (
            <linearGradient key={s.key} id={`${uid}-a-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis {...catAxisProps} />
        <YAxis type="number" {...numericTick} width={40} tickFormatter={formatCompact} label={axisLabel(chart.yLabel, "y")} />
        <Tooltip cursor={{ stroke: CURSOR_LINE, strokeWidth: 1, strokeDasharray: "3 3" }} content={cartesianTooltip} />
        {chart.series.map((s) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2.5}
            fill={`url(#${uid}-a-${s.key})`}
            stackId={chart.stacked ? "stack" : undefined}
            activeDot={{ r: 4, strokeWidth: 0 }}
            {...anim}
          />
        ))}
      </AreaChart>
    );
  } else {
    body = (
      <LineChart data={chart.rows} margin={cartesianMargin}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis {...catAxisProps} />
        <YAxis type="number" {...numericTick} width={40} tickFormatter={formatCompact} label={axisLabel(chart.yLabel, "y")} />
        <Tooltip cursor={{ stroke: CURSOR_LINE, strokeWidth: 1, strokeDasharray: "3 3" }} content={cartesianTooltip} />
        {chart.series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            {...anim}
          />
        ))}
      </LineChart>
    );
  }

  const legend =
    !chart.singleSeries && (chart.legend === "top" || chart.legend === "bottom") ? (
      <CartesianLegend series={chart.series} />
    ) : null;

  return (
    <div>
      {chart.legend === "top" && legend}
      <ResponsiveContainer width="100%" height={height}>
        {body}
      </ResponsiveContainer>
      {chart.legend === "bottom" && legend}
    </div>
  );
});
